import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Breadcrumb from "@components/UI/Breadcrumb";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import SeverityBadge from "@components/UI/SecurityInformation/SeverityBadge";
import CustomTextarea from "@components/Forms/CustomTextarea";
import { ChevronDownIcon, CopyIcon } from "@components/Icons";
import { useTheme } from "@hooks/useTheme";
import { CVE_LINK } from "@constants/index";
import { formatDateTime } from "@utils/common";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import paths from "@router/paths";
import { translateVulnStatus } from "./cveVulnStatus";

const TEST_ID = "cve-browsing-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;

const SCORE_COLORS = {
  critical: { stroke: "#ef4444", text: "text-red-500" },
  high: { stroke: "#f59e0b", text: "text-amber-400" },
  medium: { stroke: "#42C83C", text: "text-[#42C83C]" },
  low: { stroke: "#38bdf8", text: "text-sky-400" },
  none: { stroke: "#9ca3af", text: "text-gray-400" },
};

const displayValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return value;
};

const parseScore = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const score = Number.parseFloat(String(value));
  return Number.isFinite(score) ? score : null;
};

const scoreLevel = (score) => {
  if (score == null) {
    return "none";
  }
  if (score >= 9) {
    return "critical";
  }
  if (score >= 7) {
    return "high";
  }
  if (score >= 4) {
    return "medium";
  }
  if (score > 0) {
    return "low";
  }
  return "none";
};

const preferredSeverity = (detail) => detail?.cvssV40Severity || detail?.cvssV31Severity || null;

const buildCvssMetrics = (detail, t) => [
  {
    key: "v40",
    label: t("cve-browsing.cvss-v40"),
    score: detail?.cvssV40Score,
    severity: detail?.cvssV40Severity,
    vector: detail?.cvssV40Vector,
  },
  {
    key: "v31",
    label: t("cve-browsing.cvss-v31"),
    score: detail?.cvssV31Score,
    severity: detail?.cvssV31Severity,
    vector: detail?.cvssV31Vector,
  },
];

const CvssMetricCard = ({ metric, na, dataTestId }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-6">
        <CvssScoreRing score={metric.score} label={metric.label} dataTestId={`${dataTestId}-ring`} />
        <div className="flex-1 space-y-3 pt-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase ${colors.textSecondary}`}>
              {t("cve-browsing.cvss-severity")}
            </span>
            {metric.severity ? (
              <SeverityBadge severity={metric.severity} data-testid={`${dataTestId}-severity`} />
            ) : (
              <span className={`text-sm opacity-50 ${colors.textSecondary}`}>{na}</span>
            )}
          </div>
          <VectorBlock
            vector={metric.vector}
            label={t("cve-browsing.cvss-vector")}
            dataTestId={`${dataTestId}-vector`}
          />
        </div>
      </div>
    </div>
  );
};

const CvssScoreRing = ({ score, label, dataTestId }) => {
  const { colors } = useTheme();
  const parsed = parseScore(score);
  const level = scoreLevel(parsed);
  const palette = SCORE_COLORS[level];
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const progress = parsed == null ? 0 : Math.min(Math.max(parsed / 10, 0), 1);
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="flex flex-col items-center gap-2" data-testid={dataTestId}>
      <div className="relative h-28 w-28">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden="true">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className={`${colors.textSecondary} opacity-20`}
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={palette.stroke}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-2xl font-bold ${palette.text}`}>
            {parsed == null ? "N/A" : parsed.toFixed(1)}
          </span>
        </div>
      </div>
      <span className={`text-xs font-semibold uppercase tracking-wide ${colors.textSecondary}`}>{label}</span>
    </div>
  );
};

const VectorBlock = ({ vector, label, dataTestId }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const na = t("common.n-a", "N/A");
  const value = displayValue(vector, na);
  const canCopy = Boolean(vector);

  const handleCopy = async () => {
    if (!canCopy) {
      return;
    }
    try {
      await navigator.clipboard.writeText(String(vector));
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <div className={`text-xs font-semibold uppercase tracking-wide ${colors.textSecondary}`}>{label}</div>
      <div
        className={`relative rounded-md border px-3 py-2 pr-10 font-mono text-xs break-all ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary}`}
      >
        <span className={!canCopy ? `${colors.textSecondary}` : ""}>{value}</span>
        {canCopy && (
          <button
            type="button"
            onClick={handleCopy}
            title={t("cve-browsing.copy-vector")}
            aria-label={t("cve-browsing.copy-vector")}
            className={`absolute top-2 right-2 ${colors.textActive} hover:opacity-80`}
            data-testid={`${dataTestId}-copy`}
          >
            <CopyIcon size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const MetadataRow = ({ label, children, testId }) => {
  const { colors } = useTheme();
  return (
    <div data-testid={testId}>
      <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>{label}</label>
      <div className={`text-sm font-medium ${colors.textMessage}`}>{children}</div>
    </div>
  );
};

const DetailSection = ({ title, sectionId, children, className = "" }) => {
  const { colors } = useTheme();

  return (
    <section
      className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary} border ${className}`}
      data-testid={tid(sectionId)}
    >
      <div className="mb-2 flex items-center">
        <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid(`${sectionId}-title`)}>
          {title}
        </h2>
      </div>
      <div data-testid={tid(`${sectionId}-body`)}>{children}</div>
    </section>
  );
};

const groupAffectedProducts = (items) => {
  const groups = new Map();
  for (const item of items) {
    const vendor = item?.vendor ?? "";
    const product = item?.product ?? "";
    const key = `${String(vendor).toLowerCase()}|${String(product).toLowerCase()}`;
    if (!groups.has(key)) {
      groups.set(key, { key, vendor, product, versions: [] });
    }
    groups.get(key).versions.push(item);
  }
  return Array.from(groups.values());
};

export default function CveAnalysisDetail() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { cveAnalysisSeq } = useParams();
  const [detail, setDetail] = useState(null);
  const [expandedProductKeys, setExpandedProductKeys] = useState(() => new Set());
  const criteriaTooltipId = "cve-browsing-criteria-tooltip";

  const getDetail = useMutationData(
    `${queryKeys.getCveAnalysisDetail}/${cveAnalysisSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setDetail(response);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: paths.cveAnalysis } });
          return;
        }
        showErrorToast(t("preferences.loading-data-error"));
        console.error("Error during get CVE analysis detail:", error);
      },
    },
  );

  useEffect(() => {
    if (cveAnalysisSeq) {
      getDetail.mutate();
    }
  }, [cveAnalysisSeq]);

  useEffect(() => {
    setExpandedProductKeys(new Set());
  }, [cveAnalysisSeq]);

  const affectedProducts = Array.isArray(detail?.affectedProducts) ? detail.affectedProducts : [];
  const groupedAffectedProducts = groupAffectedProducts(affectedProducts);
  const na = t("common.n-a", "N/A");
  const severity = preferredSeverity(detail);
  const cvssMetrics = buildCvssMetrics(detail, t);

  const toggleProductGroup = (key) => {
    setExpandedProductKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid={tid("page")}>
      <div className="mb-6" data-testid={tid("header")}>
        <Breadcrumb
          data-testid={tid("breadcrumb")}
          items={[
            { label: t("navbar.cve-browsing"), href: paths.cveAnalysis },
            { label: t("cve-browsing.detail-title") },
          ]}
          className="mb-4"
        />
        <div className="flex flex-wrap items-center gap-3" data-testid={tid("title-row")}>
          {detail?.cveId ? (
            <a
              className={`text-[28px] uppercase font-bold truncate leading-tight ${colors.textActive}`}
              href={`${CVE_LINK}/${detail.cveId}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={tid("title")}
              onClick={(e) => {
                e.preventDefault();
                window.open(`${CVE_LINK}/${detail.cveId}`, "_blank", "noopener,noreferrer");
              }}
            >
              {detail.cveId}
            </a>
          ) : (
            <h1 className={`text-[28px] uppercase font-bold truncate ${colors.textPrimary}`} data-testid={tid("title")}>
              {getDetail.isPending ? t("cve-browsing.loading") : t("cve-browsing.detail-title")}
            </h1>
          )}
          {severity ? (
            <SeverityBadge
              severity={severity}
              className="min-w-[5rem] w-auto px-2 py-0.5 text-xs"
              data-testid={tid("severity-badge")}
            />
          ) : (
            !getDetail.isPending && (
              <span
                className="inline-flex items-center rounded border border-gray-500 px-2 py-0.5 text-xs font-medium text-gray-400"
                data-testid={tid("severity-badge")}
              >
                {na}
              </span>
            )
          )}
        </div>
      </div>

      {getDetail.isPending && !detail ? (
        <div className="flex justify-center py-16" data-testid={tid("loading")}>
          <LoadingSpinner />
        </div>
      ) : (
        <div className="pr-4 pb-8" data-testid={tid("content")}>
          <DetailSection title={t("cve-browsing.system-metadata")} sectionId="system-metadata">
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4 pt-2" data-testid={tid("meta-grid")}>
              <MetadataRow label={t("cve-browsing.status")} testId={tid("meta-status")}>
                {detail?.deletedFlag === "Y"
                  ? t("cve-browsing.status-masked")
                  : t("cve-browsing.status-active")}
              </MetadataRow>
              <MetadataRow label={t("cve-browsing.vuln-status")} testId={tid("meta-vuln-status")}>
                {translateVulnStatus(t, detail?.vulnStatus, na)}
              </MetadataRow>
              <MetadataRow label={t("cve-browsing.published-at")} testId={tid("meta-published")}>
                {detail?.publishedAt ? formatDateTime(detail.publishedAt) : na}
              </MetadataRow>
              <MetadataRow label={t("common.last-modified-date")} testId={tid("meta-last-modified")}>
                {detail?.lastModifiedAt ? formatDateTime(detail.lastModifiedAt) : na}
              </MetadataRow>
              <MetadataRow label={t("cve-browsing.created-at")} testId={tid("meta-created")}>
                {detail?.createdAt ? formatDateTime(detail.createdAt) : na}
              </MetadataRow>
              <MetadataRow label={t("cve-browsing.synced-at")} testId={tid("meta-synced")}>
                {detail?.syncedAt ? formatDateTime(detail.syncedAt) : na}
              </MetadataRow>
              <MetadataRow label={t("cve-browsing.updated-at")} testId={tid("meta-updated")}>
                {detail?.updatedAt ? formatDateTime(detail.updatedAt) : na}
              </MetadataRow>
              <MetadataRow label={t("cve-browsing.dataset-version")} testId={tid("meta-dataset")}>
                {displayValue(detail?.datasetVersion, na)}
              </MetadataRow>
            </div>

            <div
              className={`flex pt-6 mt-6 border-t border-dashed ${colors.borderPrimary}`}
              data-testid={tid("description-row")}
            >
              <div className="w-24 flex-shrink-0">
                <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`}>
                  {t("cve-browsing.description")}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <CustomTextarea
                  data-testid={tid("description")}
                  rows={4}
                  value={detail?.description || ""}
                  title={detail?.description}
                  readOnly={true}
                />
              </div>
            </div>
          </DetailSection>

          <DetailSection title={t("cve-browsing.cvss-information")} sectionId="cvss">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 pt-2">
              {cvssMetrics.map((metric) => (
                <CvssMetricCard
                  key={metric.key}
                  metric={metric}
                  na={na}
                  dataTestId={tid(`cvss-${metric.key}`)}
                />
              ))}
            </div>
          </DetailSection>

          <DetailSection title={t("cve-browsing.affected-products-detail")} sectionId="affected-products">
            <div className="mt-2 space-y-3" data-testid={tid("affected-table")}>
              {groupedAffectedProducts.length === 0 ? (
                <div
                  className={`rounded-lg border px-4 py-4 text-sm ${colors.borderPrimary} ${colors.bgPrimary} ${colors.textSecondary}`}
                  data-testid={tid("affected-empty")}
                >
                  {t("common.no-data-to-display")}
                </div>
              ) : (
                groupedAffectedProducts.map((group, groupIndex) => {
                  const isExpanded = expandedProductKeys.has(group.key);
                  const vendorLabel = displayValue(group.vendor, na);
                  const productLabel = displayValue(group.product, na);
                  const bodyId = tid(`affected-group-body-${groupIndex}`);
                  return (
                    <div
                      key={group.key}
                      className={`overflow-hidden rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary}`}
                      data-testid={tid(`affected-group-${groupIndex}`)}
                    >
                      <button
                        type="button"
                        className={`grid w-full grid-cols-[minmax(10rem,28%)_1fr] items-center gap-8 px-4 py-3 text-left ${colors.hoverTableRow}`}
                        onClick={() => toggleProductGroup(group.key)}
                        aria-expanded={isExpanded}
                        aria-controls={bodyId}
                        data-testid={tid(`affected-group-toggle-${groupIndex}`)}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <ChevronDownIcon
                            aria-hidden="true"
                            className={`h-4 w-4 shrink-0 transition-transform duration-200 ${colors.textSecondary} ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                          <div className="min-w-0">
                            <div className={`text-[10px] font-semibold uppercase tracking-wider ${colors.textSubTitle}`}>
                              {t("cve-browsing.vendor")}
                            </div>
                            <div className={`truncate text-sm font-medium ${colors.textPrimary}`} title={vendorLabel}>
                              {vendorLabel}
                            </div>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className={`text-[10px] font-semibold uppercase tracking-wider ${colors.textSubTitle}`}>
                            {t("cve-browsing.product")}
                          </div>
                          <div className={`truncate text-sm font-medium ${colors.textPrimary}`} title={productLabel}>
                            {productLabel}
                          </div>
                        </div>
                      </button>

                      {isExpanded && (
                        <div
                          id={bodyId}
                          className={`border-t ${colors.borderPrimary} ${colors.bgSecondary}`}
                          data-testid={bodyId}
                        >
                          <div className="overflow-x-auto px-3 py-3">
                            <div className={`overflow-hidden rounded-md border ${colors.borderPrimary}`}>
                              <table className="w-full table-fixed">
                                <thead className={`${colors.bgPrimary} border-b ${colors.borderPrimary}`}>
                                  <tr>
                                    <th
                                      className={`${colors.textMuted} px-5 py-2.5 text-left text-[10px] font-medium tracking-wider uppercase`}
                                      style={{ width: "30%" }}
                                    >
                                      {t("cve-browsing.affected-version-range")}
                                    </th>
                                    <th
                                      className={`${colors.textMuted} px-5 py-2.5 text-left text-[10px] font-medium tracking-wider uppercase`}
                                      style={{ width: "24%" }}
                                    >
                                      {t("cve-browsing.fixed-version")}
                                    </th>
                                    <th
                                      className={`${colors.textMuted} px-5 py-2.5 text-left text-[10px] font-medium tracking-wider uppercase`}
                                      style={{ width: "46%" }}
                                    >
                                      {t("cve-browsing.criteria")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className={colors.bgTableBody}>
                                  {group.versions.map((item, versionIndex) => {
                                    const fixedVersion = item.fixedVersion;
                                    const hasFix = Boolean(fixedVersion);
                                    const criteria = displayValue(item.criteria, na);
                                    const nestedStripe =
                                      versionIndex % 2 === 0 ? colors.bgTertiary : colors.bgPrimary;
                                    return (
                                      <tr
                                        key={`${group.key}-${item.criteria || versionIndex}`}
                                        className={nestedStripe}
                                        data-testid={tid(`affected-row-${groupIndex}-${versionIndex}`)}
                                      >
                                        <td className={`px-5 py-2.5 text-sm ${colors.textPrimary}`}>
                                          <div
                                            className="truncate"
                                            title={displayValue(item.affectedVersionRange, na)}
                                          >
                                            {displayValue(item.affectedVersionRange, na)}
                                          </div>
                                        </td>
                                        <td
                                          className={`px-5 py-2.5 text-sm ${
                                            hasFix
                                              ? "font-semibold text-emerald-500"
                                              : `${colors.textSecondary}`
                                          }`}
                                        >
                                          <div className="truncate" title={displayValue(fixedVersion, na)}>
                                            {displayValue(fixedVersion, na)}
                                          </div>
                                        </td>
                                        <td className={`px-5 py-2.5 text-sm ${colors.textPrimary}`}>
                                          <div
                                            className="truncate font-mono text-xs cursor-help"
                                            data-tooltip-id={criteriaTooltipId}
                                            data-tooltip-content={criteria}
                                          >
                                            {criteria}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <Tooltip id={criteriaTooltipId} place="top" className="z-50 max-w-md break-all" />
          </DetailSection>
        </div>
      )}
    </div>
  );
}
